import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Sun, Moon, Menu, Shield, User, LogOut, MessageSquare, LayoutDashboard, Plus, Car, ShieldCheck, Settings, Wallet, Wrench, BookOpen, Gift, Info } from 'lucide-react';
import Logo from './Logo';
import { useState } from 'react';

// Admin emails
const ADMIN_EMAILS = ["daniel.tawiah@gmail.com", "info@iassistpro.com", "admin@autotrustgh.com"];

export default function Navbar() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  
  const isAdmin = user && (ADMIN_EMAILS.includes(user.email) || user.admin_role);

  return (
    <nav data-testid="navbar" className="sticky top-0 z-40 backdrop-blur-xl bg-background/80 border-b border-border/50">
      <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
        <Logo size="md" linkTo="/" dataTestId="nav-logo" />

        <div className="hidden md:flex items-center gap-1">
          <Link to="/browse">
            <Button variant="ghost" className="text-sm gap-2" data-testid="nav-browse">
              <Car className="w-4 h-4" /> Browse
            </Button>
          </Link>
          {user && (
            <Link to="/sell">
              <Button variant="ghost" className="text-sm gap-2" data-testid="nav-sell">
                <Plus className="w-4 h-4" /> Sell
              </Button>
            </Link>
          )}
          <Link to="/trust">
            <Button variant="ghost" className="text-sm gap-2" data-testid="nav-trust">
              <ShieldCheck className="w-4 h-4" /> Trust
            </Button>
          </Link>
          <Link to="/about">
            <Button variant="ghost" className="text-sm gap-2" data-testid="nav-about">
              <Info className="w-4 h-4" /> About
            </Button>
          </Link>
          {user && (
            <Link to="/messages">
              <Button variant="ghost" className="text-sm gap-2" data-testid="nav-messages">
                <MessageSquare className="w-4 h-4" /> Messages
              </Button>
            </Link>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle" className="rounded-full">
            {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </Button>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2 rounded-full" data-testid="user-menu-trigger">
                  <div className="w-7 h-7 rounded-full bg-gold/20 flex items-center justify-center text-sm font-semibold text-gold">
                    {user.name?.[0]?.toUpperCase() || 'U'}
                  </div>
                  <span className="hidden sm:inline text-sm">{user.name?.split(' ')[0]}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="menu-dashboard">
                  <LayoutDashboard className="w-4 h-4 mr-2" /> Dashboard
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/kyc')} data-testid="menu-kyc">
                  <ShieldCheck className="w-4 h-4 mr-2" /> Verify Identity
                  {user.kyc_verified && (
                    <span className="ml-auto text-trust-green text-xs">Verified</span>
                  )}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/messages')} data-testid="menu-messages">
                  <MessageSquare className="w-4 h-4 mr-2" /> Messages
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/payout-settings')} data-testid="menu-payout">
                  <Wallet className="w-4 h-4 mr-2" /> Payout Settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/referral')} data-testid="menu-referral">
                  <Gift className="w-4 h-4 mr-2" /> Refer & Earn
                </DropdownMenuItem>
                {isAdmin && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="menu-admin" className="text-yellow-500">
                      <Settings className="w-4 h-4 mr-2" /> Admin Dashboard
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => { logout(); navigate('/'); }} data-testid="menu-logout">
                  <LogOut className="w-4 h-4 mr-2" /> Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div className="flex items-center gap-2">
              <Link to="/login">
                <Button variant="ghost" size="sm" data-testid="nav-login">Sign In</Button>
              </Link>
              <Link to="/register">
                <Button size="sm" className="bg-gold text-black hover:brightness-110 font-semibold" data-testid="nav-register">
                  Get Started
                </Button>
              </Link>
            </div>
          )}

          <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-toggle">
            <Menu className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-border/50 bg-background p-4 space-y-2">
          <Link to="/browse" onClick={() => setMobileOpen(false)}>
            <Button variant="ghost" className="w-full justify-start gap-2"><Car className="w-4 h-4" /> Browse Marketplace</Button>
          </Link>
          {user && (
            <>
              <Link to="/sell" onClick={() => setMobileOpen(false)}>
                <Button variant="ghost" className="w-full justify-start gap-2"><Plus className="w-4 h-4" /> Sell</Button>
              </Link>
              <Link to="/dashboard" onClick={() => setMobileOpen(false)}>
                <Button variant="ghost" className="w-full justify-start gap-2"><LayoutDashboard className="w-4 h-4" /> Dashboard</Button>
              </Link>
            </>
          )}
        </div>
      )}
    </nav>
  );
}
